import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * The project's custom `--text-*` font-size scale (globals.css `@theme`).
 * tailwind-merge's default config only recognizes Tailwind's stock size
 * keys (xs/sm/base/lg/...) for the `font-size` group, so a bare numeric key
 * like `text-3.5` falls through to the `text-color` group instead — where
 * it silently collides with (and gets dropped by) any `text-{color}` class
 * in the same cn() call. Listing the scale here keeps size and color in
 * their own conflict groups.
 */
const FONT_SIZE_SCALE = [
  "2.5",
  "3",
  "3.5",
  "4",
  "4.5",
  "5",
  "5.5",
  "6",
  "6.5",
  "7",
  "7.5",
  "8",
  "9",
  "10",
  "11",
  "12",
  "13",
  "15",
  "18",
  "24",
  "32",
];

/**
 * The project's custom `--background-image-*` tokens (globals.css `@theme`)
 * — named gradients/textures consumed as `bg-{name}`. Same collision as
 * font-size: tailwind-merge doesn't recognize these as the `bg-image` group,
 * so they fall through to `bg-color` and silently drop (or get dropped by)
 * an actual background-color class like `bg-bg-elevated` in the same call.
 */
const BG_IMAGE_SCALE = [
  "ab-gold-corner",
  "ab-gold-side",
  "abilities-tab-gradient",
  "account-credits-gradient",
  "account-credits-gradient-vertical",
  "account-profile-gradient",
  "acct-card-135",
  "acct-card-150",
  "agent-status-line-gradient",
  "agents-gradient",
  "brand-loader-gradient",
  "brand-loader-gradient-2",
  "brand-loader-gradient-3",
  "browser-canvas-inline",
  "cards-gradient",
  "checkpoint-block-inline",
  "compaction-block-gradient",
  "conversation-view-gradient",
  "delegation-card-gradient",
  "embedded-browser-chrome-inline",
  "embedded-browser-chrome-inline-2",
  "fade-right-elevated",
  "fd-avatar",
  "fd-grain",
  "fd-porcelain",
  "file-preview-overlay-gradient",
  "gold-dot",
  "gold-glow",
  "gold-molten-160",
  "gold-molten-180",
  "gold-molten-180-55",
  "gold-molten-180-60",
  "gold-molten-radial",
  "gold-molten-sheen",
  "gold-sweep-112",
  "graduation-card-inline",
  "greeting-view-gradient",
  "inbox-gradient",
  "inbox-gradient-2",
  "marketplace-grid-inline",
  "message-list-gradient",
  "model-selector-gradient",
  "model-selector-gradient-2",
  "purchase-hold",
  "regular-tool-block-gradient",
  "shared-gradient",
  "shelf-section-gradient",
  "shimmer-elevated",
  "shimmer-sweep",
  "shimmer-tinted",
  "skeleton-shimmer",
  "sl-shimmer",
  "stone-shimmer",
  "story-view-gradient",
  "suggestion-card-inline",
  "thinking-block-gradient",
  "todo-block-gradient",
];

/**
 * The project's custom `--shadow-*` / `--drop-shadow-*` tokens. Same
 * collision again: an unrecognized `shadow-{name}` falls through to the
 * `shadow-color` group instead of `shadow`, so two unrelated custom shadows
 * (or a shadow and a `shadow-{tailwind-color}`) can silently drop each
 * other.
 */
const SHADOW_SCALE = [
  "ab-card-shadow",
  "abilities-tab-2",
  "ability-recommendation-card",
  "ability-row",
  "access-level-picker",
  "access-level-picker-2",
  "account-credits",
  "account-credits-2",
  "account-profile",
  "account-settings",
  "agent-tabs",
  "agent-tabs-2",
  "app-card",
  "app-shell",
  "archive-overlay",
  "archive-overlay-2",
  "artifacts",
  "artifacts-2",
  "brief-overlay",
  "cards",
  "cards-2",
  "command-palette",
  "compaction-block",
  "composer-desk",
  "composer-desk-2",
  "connect-channels-modal-inline",
  "connect-channels-modal-inline-2",
  "connect-channels-modal-inline-3",
  "connectivity-capsule",
  "conversation-view",
  "country-select",
  "creations-grid-view",
  "credit-exhausted-modal-inline",
  "delete-chip",
  "fd-btn",
  "fd-card",
  "fd-face",
  "fd-frame",
  "fd-menu",
  "fd-opt",
  "fd-opt-hover",
  "fd-opt-on",
  "fd-pill",
  "fd-say",
  "fd-seat",
  "file-preview",
  "file-preview-2",
  "file-preview-overlay",
  "file-preview-overlay-2",
  "file-quick-look-card",
  "file-quick-look-card-2",
  "files-tree-row-inline",
  "finder-icons",
  "finder-icons-2",
  "glow",
  "home",
  "home-dashboard",
  "keyring",
  "keyring-2",
  "keyring-3",
  "keyring-4",
  "keyring-5",
  "left-sidebar",
  "left-sidebar-2",
  "lightbox",
  "listening-row",
  "marketplace-grid",
  "marketplace-grid-inline",
  "memory-dossier",
  "message-input",
  "message-input-2",
  "model-selector",
  "model-selector-2",
  "model-selector-3",
  "model-selector-4",
  "model-selector-5",
  "model-selector-6",
  "parked-browser-chip",
  "parked-browser-chip-2",
  "proposal-detail-modal",
  "protected-route-error",
  "raw-glow",
  "raw-lg",
  "raw-md",
  "raw-sm",
  "raw-xl",
  "recipient-picker-inline",
  "routine-detail-view",
  "routine-detail-view-2",
  "routine-list-pane",
  "routines-page",
  "routines-page-2",
  "shelf-section",
  "shelf-section-2",
  "skill-picker",
  "status-glow-amber",
  "status-glow-blue",
  "status-glow-success",
  "story-view",
  "story-view-2",
  "story-view-3",
  "task",
  "task-feed",
  "task-feed-2",
  "task-header-bar",
  "task-header-bar-2",
  "task-picker",
  "todo-block",
  "todo-block-2",
  "working-now-section",
  "working-now-section-2",
];

const DROP_SHADOW_SCALE = [
  "abilities-tab",
  "file-preview-3",
  "finder-tile",
  "story-stage",
];

/**
 * A THIRD flavor of the same collision, this time not about custom theme
 * names at all: tailwind-merge groups an arbitrary CSS *property* class like
 * `[border-color:var(--x)]` under a synthetic id (`arbitrary..border-color`)
 * that is never registered as conflicting with the matching NAMED utility's
 * classGroup (`border-color`, for `border-red-500` / `border-border-subtle`
 * / etc). So `cn("border-border-subtle", "[border-color:var(--x)]")` keeps
 * BOTH classes — no override happens, and which one visually wins depends on
 * generated-CSS source order, not on cn() call order. This project uses the
 * arbitrary-property form extensively (`[border-color:...]` alone: 100+
 * call sites) specifically to reach CSS custom properties Tailwind's named
 * color utilities can't reference, so both forms need to conflict for real.
 * Each pair below is registered in both directions, since tailwind-merge
 * only walks a class's own conflict list to evict EARLIER classes — it
 * doesn't infer the reverse relationship automatically.
 */
const ARBITRARY_COLOR_PROPERTY_CONFLICTS: Record<string, string[]> = {
  "border-color": ["arbitrary..border-color"],
  "arbitrary..border-color": ["border-color"],
  "border-color-t": ["arbitrary..border-top-color"],
  "arbitrary..border-top-color": ["border-color-t"],
  "border-color-b": ["arbitrary..border-bottom-color"],
  "arbitrary..border-bottom-color": ["border-color-b"],
  "border-color-l": ["arbitrary..border-left-color"],
  "arbitrary..border-left-color": ["border-color-l"],
  "border-color-r": ["arbitrary..border-right-color"],
  "arbitrary..border-right-color": ["border-color-r"],
  "text-decoration-color": ["arbitrary..text-decoration-color"],
  "arbitrary..text-decoration-color": ["text-decoration-color"],
  fill: ["arbitrary..fill"],
  "arbitrary..fill": ["fill"],
  stroke: ["arbitrary..stroke"],
  "arbitrary..stroke": ["stroke"],
  // Tailwind's classGroup for the box-shadow utility is named "shadow"; the
  // CSS property inside the brackets is "box-shadow", so the synthetic id
  // is keyed off that property name, not the classGroup name.
  shadow: ["arbitrary..box-shadow"],
  "arbitrary..box-shadow": ["shadow"],
  "bg-color": ["arbitrary..background-color", "arbitrary..background"],
  "arbitrary..background-color": ["bg-color"],
  "arbitrary..background": ["bg-color"],
};

const RADIUS_SCALE = [
  "2",
  "4",
  "6",
  "7",
  "8",
  "10",
  "12",
  "16",
  "20",
  "24",
  "28",
  "32",
  "100",
];

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [{ text: FONT_SIZE_SCALE }],
      "bg-image": [{ bg: BG_IMAGE_SCALE }],
      shadow: [{ shadow: SHADOW_SCALE }],
      "drop-shadow": [{ "drop-shadow": DROP_SHADOW_SCALE }],
      rounded: [{ rounded: RADIUS_SCALE }],
      "rounded-t": [{ "rounded-t": RADIUS_SCALE }],
      "rounded-b": [{ "rounded-b": RADIUS_SCALE }],
      "rounded-l": [{ "rounded-l": RADIUS_SCALE }],
      "rounded-r": [{ "rounded-r": RADIUS_SCALE }],
      "rounded-tl": [{ "rounded-tl": RADIUS_SCALE }],
      "rounded-tr": [{ "rounded-tr": RADIUS_SCALE }],
      "rounded-bl": [{ "rounded-bl": RADIUS_SCALE }],
      "rounded-br": [{ "rounded-br": RADIUS_SCALE }],
    },
    conflictingClassGroups: ARBITRARY_COLOR_PROPERTY_CONFLICTS,
  },
});

/**
 * Merge Tailwind CSS classes with proper conflict resolution.
 * Uses clsx for conditional classes and tailwind-merge to handle conflicts.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
